/**
 * Zustand store for Calendar Event Editor
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  CalendarEventDto,
  CalendarEventStatus,
  EventOwnerEntityType,
} from '@api/types/calendarEvent'
import {
  CalendarEventFormData,
  CalendarEventSectionId,
  EventOwnerSelection,
  CalendarEventValidationState,
  createInitialFormData,
  eventToFormData,
  validateFormData,
  hasFormChanges,
  isEventDraft,
  isEventPublished,
} from '../types/calendar-event-editor.types'

// ====================
// STATE INTERFACE
// ====================

export interface CalendarEventEditorState {
  // Core event data
  eventId?: string
  event?: CalendarEventDto

  // Form data
  formData: CalendarEventFormData
  originalFormData: CalendarEventFormData | null

  // Owner selection (for group/business events)
  ownerSelection: EventOwnerSelection | null

  // Tags
  selectedTags: string[]

  // UI state
  activeSection: CalendarEventSectionId
  isLoading: boolean
  isDirty: boolean
  isSaving: boolean
  isPublishing: boolean

  // Validation
  validation: CalendarEventValidationState

  // Actions
  loadEvent: (event: CalendarEventDto) => void
  updateFormData: (updates: Partial<CalendarEventFormData>) => void
  setOwnerSelection: (owner: EventOwnerSelection | null) => void
  setActiveSection: (section: CalendarEventSectionId) => void
  setSelectedTags: (tags: string[]) => void
  toggleTag: (tagId: string) => void
  setIsLoading: (isLoading: boolean) => void
  setIsSaving: (isSaving: boolean) => void
  setIsPublishing: (isPublishing: boolean) => void
  setIsDirty: (isDirty: boolean) => void
  validate: () => CalendarEventValidationState
  reset: () => void
  resetForm: () => void
}

// ====================
// INITIAL STATE
// ====================

const initialFormData = createInitialFormData()

const initialState = {
  eventId: undefined,
  event: undefined,
  formData: initialFormData,
  originalFormData: null,
  ownerSelection: null,
  selectedTags: [],
  activeSection: 'details' as CalendarEventSectionId,
  isLoading: false,
  isDirty: false,
  isSaving: false,
  isPublishing: false,
  validation: { isValid: true, errors: {} },
}

// ====================
// STORE
// ====================

export const useCalendarEventEditorStore = create<CalendarEventEditorState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Load event from API
      loadEvent: (event: CalendarEventDto) => {
        const formData = eventToFormData(event)
        set(
          {
            eventId: event.id,
            event,
            formData,
            originalFormData: formData,
            selectedTags: event.tags || [],
            isDirty: false,
            validation: validateFormData(formData),
          },
          false,
          'loadEvent'
        )
      },

      // Update form data
      updateFormData: (updates: Partial<CalendarEventFormData>) => {
        set(
          (state) => {
            const newFormData = { ...state.formData, ...updates }
            const isDirty = hasFormChanges(state.originalFormData, newFormData)
            return {
              formData: newFormData,
              isDirty,
              validation: validateFormData(newFormData),
            }
          },
          false,
          'updateFormData'
        )
      },

      // Set owner selection
      setOwnerSelection: (owner: EventOwnerSelection | null) => {
        set({ ownerSelection: owner, isDirty: true }, false, 'setOwnerSelection')
      },

      // Set active section
      setActiveSection: (section: CalendarEventSectionId) => {
        set({ activeSection: section }, false, 'setActiveSection')
      },

      // Set selected tags
      setSelectedTags: (tags: string[]) => {
        set((state) => {
          const originalTags = state.event?.tags || []
          const tagsChanged =
            tags.length !== originalTags.length ||
            !tags.every((t) => originalTags.includes(t))
          return {
            selectedTags: tags,
            isDirty: state.isDirty || tagsChanged,
          }
        }, false, 'setSelectedTags')
      },

      // Toggle a single tag
      toggleTag: (tagId: string) => {
        set((state) => {
          const currentTags = [...state.selectedTags]
          const index = currentTags.indexOf(tagId)
          if (index > -1) {
            currentTags.splice(index, 1)
          } else {
            currentTags.push(tagId)
          }
          const originalTags = state.event?.tags || []
          const tagsChanged =
            currentTags.length !== originalTags.length ||
            !currentTags.every((t) => originalTags.includes(t))
          return {
            selectedTags: currentTags,
            isDirty: state.isDirty || tagsChanged,
          }
        }, false, 'toggleTag')
      },

      // Set loading state
      setIsLoading: (isLoading: boolean) => {
        set({ isLoading }, false, 'setIsLoading')
      },

      // Set saving state
      setIsSaving: (isSaving: boolean) => {
        set({ isSaving }, false, 'setIsSaving')
      },

      // Set publishing state
      setIsPublishing: (isPublishing: boolean) => {
        set({ isPublishing }, false, 'setIsPublishing')
      },

      // Set dirty state manually
      setIsDirty: (isDirty: boolean) => {
        set({ isDirty }, false, 'setIsDirty')
      },

      // Validate form data
      validate: () => {
        const state = get()
        const validation = validateFormData(state.formData)
        set({ validation }, false, 'validate')
        return validation
      },

      // Reset to initial state
      reset: () => {
        set({ ...initialState, formData: createInitialFormData() }, false, 'reset')
      },

      // Reset form to original values (discard changes)
      resetForm: () => {
        set((state) => {
          if (state.originalFormData) {
            return {
              formData: { ...state.originalFormData },
              selectedTags: state.event?.tags || [],
              isDirty: false,
              validation: validateFormData(state.originalFormData),
            }
          }
          return {
            formData: createInitialFormData(),
            selectedTags: [],
            isDirty: false,
            validation: { isValid: true, errors: {} },
          }
        }, false, 'resetForm')
      },
    }),
    { name: 'CalendarEventEditorStore' }
  )
)

// ====================
// SELECTORS
// ====================

export const selectFormData = (state: CalendarEventEditorState) => state.formData
export const selectActiveSection = (state: CalendarEventEditorState) => state.activeSection
export const selectSelectedTags = (state: CalendarEventEditorState) => state.selectedTags
export const selectIsDirty = (state: CalendarEventEditorState) => state.isDirty
export const selectValidation = (state: CalendarEventEditorState) => state.validation
export const selectIsValid = (state: CalendarEventEditorState) => state.validation.isValid

/**
 * Check if event is in draft state
 */
export const selectIsEventDraft = (state: CalendarEventEditorState) => {
  if (!state.event) return true
  return isEventDraft(state.event.status)
}

/**
 * Check if event is published
 */
export const selectIsEventPublished = (state: CalendarEventEditorState) => {
  if (!state.event) return false
  return isEventPublished(state.event.status)
}

/**
 * Can save the event (must be in editable state)
 */
export const selectCanSave = (state: CalendarEventEditorState) => {
  if (!state.event) return true // New event
  const status = state.event.status
  return isEventDraft(status) || isEventPublished(status)
}

/**
 * Can publish the event (must be draft with valid data)
 */
export const selectCanPublish = (state: CalendarEventEditorState) => {
  if (!state.event) return false
  return isEventDraft(state.event.status) && state.validation.isValid
}

/**
 * Can delete the event
 */
export const selectCanDelete = (state: CalendarEventEditorState) => {
  if (!state.event) return false
  return state.event.canDelete !== false
}

/**
 * Can cancel the event (must be published)
 */
export const selectCanCancel = (state: CalendarEventEditorState) => {
  if (!state.event) return false
  return isEventPublished(state.event.status)
}
